import axios from 'axios';
import {
    GOALS,
    PROCESSES,
    EDGE_TYPE,
    FLOW_TYPE,
    GOAL_EMAIL_LINK_CLICKED,
    GOAL_EMAIL_CONFIRM,
    GOAL_TASK_COMPLETED,
    SEQUENCE_START,
    NO_VALID_INCOMING_FLOW_ERROR,
    VALID_INCOMING_FLOW,
    LEADSOURCE_TYPE,
    DECISION_TYPE,
    TIMERS,
} from '../editor.constants';
import { utils } from '../legacy-utils';
import { findIncomingCells, findLockedIncomingCells, findOutgoingCells } from './automation-edge-utils';

const BASE_FUNNEL_URL = '/app/funnel';
const NON_PROCESS_TYPES = [LEADSOURCE_TYPE, DECISION_TYPE, ...Object.values(GOALS)];
const TIMER_TYPES = [...Object.values(TIMERS)];

export const findChildren = (cellId, cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta.parent === cellId);
};

export const findParent = (parentId, cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.id === parentId);
};

export const canConfigureCellSettings = (cell, cells) => {
    if ([GOAL_TASK_COMPLETED, GOAL_EMAIL_LINK_CLICKED, GOAL_EMAIL_CONFIRM].includes(cell.type)) {
        return false;
    }

    let response = false;

    const incomingCells = findIncomingCells(cell.id, cells);

    incomingCells.forEach((source) => {
        if (source.meta?.style === FLOW_TYPE) {
            response = true;
        }
    });

    return response;
};

export const getCellById = (cellId, cells) => {
    return cells.find((cell) => cell.id === cellId);
};

export const isParentCellLocked = (cell, cells) => {
    if (!cells?.filter) return false;

    const parentSequence = cells.find((element) => element.id === cell.meta?.parent);

    return parentSequence?.value?.flowItemsLocked === '1';
};

export const isCellLocked = (cell, cells) => {
    if (cell.type === FLOW_TYPE && cell.value?.methodLocked === '1') return true;

    if (cell.type === EDGE_TYPE) {
        const sourceCell = getCellById(cell.meta.source, cells);

        if (sourceCell?.value?.methodLocked === '1') return true;

        return isParentCellLocked(sourceCell, cells);
    }

    const incomingLockedCells = findLockedIncomingCells(cell.id, cells);

    if (incomingLockedCells.length > 0) {
        return true;
    }

    return isParentCellLocked(cell, cells);
};

export const getLastCellInSequence = (sequenceId, cells) => {
    const flowStartCell = cells.find((cell) => {
        return (cell.meta.parent === sequenceId) && (cell.type === SEQUENCE_START);
    });

    const linkedCellIds = [];
    let nextCell = findOutgoingCells(flowStartCell.meta.id, cells).pop();

    if (!nextCell) return flowStartCell;


    while (nextCell && !linkedCellIds.includes(nextCell.meta.id)) {
        linkedCellIds.push(nextCell.meta.id);
        const linkedCell = findOutgoingCells(nextCell.meta.id, cells).pop();

        if (linkedCell) nextCell = linkedCell;
    }

    return nextCell;
};

export const isValid = (childType, cellType) => {
    const validIncomingFlows = Object.values(VALID_INCOMING_FLOW[cellType]);

    return validIncomingFlows.some((flow) => {
        return flow === childType;
    });
};

export const getValidIncomingFlows = (cell, cells) => {
    const flows = findIncomingCells(cell.id, cells);
    const validIncomingFlows = [];

    flows.forEach((flow) => {
        const children = findChildren(flow.id, cells);

        children.forEach((child) => {
            if (isValid(child.type, cell.type)) {
                validIncomingFlows.push(child);
            }
        });
    });

    return validIncomingFlows;
};

export const areIncomingFlowsValidForCell = (cell, cells) => {
    let result = { isValid: true };
    const validIncomingFlows = getValidIncomingFlows(cell, cells);

    if (validIncomingFlows.length < 1) {
        result = {
            isValid: false,
            messageKey: NO_VALID_INCOMING_FLOW_ERROR[cell.type],
        };
    }

    return result;
};

export const transformCell = (cell) => {
    if (cell.value) {
        if (cell.value?.deepCopy !== undefined) {
            delete cell.value.deepCopy;
        }

        if (cell.value.eventId !== undefined && cell.value.eventId !== null) {
            if (typeof cell.value.eventId === 'string') {
                cell.value.eventId = [cell.value.eventId];
            } else if (!Array.isArray(cell.value.eventId)) {
                cell.value.eventId = [cell.value.eventId.toString()];
            } else {
                cell.value.eventId = cell.value.eventId.map((id) => id.toString());
            }
        }

        if (cell.value.purchaseId !== undefined && cell.value.purchaseId !== null) {
            if (typeof cell.value.purchaseId === 'string') {
                cell.value.purchaseId = [cell.value.purchaseId];
            } else if (!Array.isArray(cell.value.purchaseId)) {
                cell.value.purchaseId = [cell.value.purchaseId.toString()];
            } else {
                cell.value.purchaseId = cell.value.purchaseId.map((id) => id.toString());
            }
        }

        if (typeof cell.value?.purchaseOption === 'string') {
            cell.value.purchaseOption = [cell.value.purchaseOption];
        }

        if (typeof cell.value?.failedPurchaseId === 'string') {
            cell.value.failedPurchaseId = utils.removeLongSuffix(cell.value.failedPurchaseId);
        }
    }

    return {
        id: cell.id,
        type: cell.type,
        value: cell.value,
    };
};

export const checkBrokenCells = async (cells) => {
    const checkedCellTypes = [GOALS.GOAL_TAG_APPLIED, GOALS.GOAL_PRODUCT_PURCHASED, GOALS.GOAL_FAILED_PURCHASE,
        PROCESSES.PROCESS_APPLY_REMOVE_TAG, PROCESSES.PROCESS_MOVE_OPPORTUNITY];
    const payload = cells
        .filter((cell) => { return checkedCellTypes.includes(cell.type); })
        .map(transformCell);

    const { data } = await axios.post(`${BASE_FUNNEL_URL}/checkBrokenCells`, payload);

    return data.filter((cell) => {
        if (cell?.ids?.length > 0) return true;

        return false;
    });
};

const getAllVertexes = (cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta?.vertex === '1');
};

const getAllReadyVertexes = (cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta?.vertex === '1' && cell.value?.ready === '1');
};

/**
 * Evaluates all draft cells for vertexes that are ready, and returns their IDs.
 *
 * @param draftCells all draft cells
 * @returns {*[]} array of IDs for all vertexes that are marked as ready
 */
const getDraftReadyVertexIds = (draftCells) => {
    const draftVertexIds = [];

    const draftVertexes = getAllReadyVertexes(draftCells);

    draftVertexes.forEach((vertex) => {
        const vertexId = vertex.id;
        const vertexType = vertex.type;

        if (NON_PROCESS_TYPES.includes(vertexType)) {
            // vertex is a goal, decision diamond or leadsource
            draftVertexIds.push(vertexId);
        } else if (vertexType === FLOW_TYPE) {
            // vertex is a flow
            draftVertexIds.push(vertexId);

            // include processes
            const children = findChildren(vertexId, draftCells);
            const startNodes = children.filter((child) => child.type === SEQUENCE_START);

            startNodes.forEach((startNode) => {
                if (startNode.value?.ready === '1') {
                    draftVertexIds.push(startNode.id);
                }

                let flowNode = startNode;
                let outgoingVertexes = [];

                do {
                    outgoingVertexes = findOutgoingCells(flowNode.id, draftCells);

                    if (outgoingVertexes.length === 1) {
                        [flowNode] = outgoingVertexes;

                        if (flowNode.value?.ready === '1') {
                            draftVertexIds.push(flowNode.id);
                        } else if (TIMER_TYPES.includes(flowNode.type)) {
                            // we don't care for anything following a timer that is not ready
                            break;
                        }
                    } else {
                        // nothing left to process
                        break;
                    }
                } while (outgoingVertexes.length);
            });
        }
    });

    return draftVertexIds;
};

/**
 * Determines if there are new or modified vertexes.
 *
 * @param draftCells all draft cells
 * @param publishedCells all published cells
 * @returns {boolean} true if there is at least one new or modified vertex, false otherwise
 */
export const hasNewOrModifiedVertexes = (draftCells, publishedCells) => {
    if (!publishedCells?.length) return false;

    const publishedVertexIds = getAllVertexes(publishedCells).map((vertex) => vertex.id);
    const draftReadyVertexes = getAllReadyVertexes(draftCells);

    return draftReadyVertexes.some((vertex) => (NON_PROCESS_TYPES.includes(vertex.type) || vertex.type === FLOW_TYPE)
            && (vertex.value.published !== '1' || !publishedVertexIds.includes(vertex.id)));
};

/**
 * Evaluates if a given (draft) vertex is new or modified.
 *
 * @param vertex the vertex to evaluate
 * @param publishedVertexIds array of vertex IDs that have already been published
 * @returns {boolean} true if the vertex is ready, and has either not been published yet, or is dirty; false otherwise
 */
const isNewOrModified = (vertex, publishedVertexIds) => {
    const vertexIsReady = vertex.value?.ready === '1';
    const vertexIsDirty = vertex.value?.published !== '1';
    const vertexIsNotPublished = !publishedVertexIds.includes(vertex.id);

    return vertexIsReady && (vertexIsDirty || vertexIsNotPublished);
};

/**
 * Evaluates vertexes to determine which are new or modified.
 *
 * @param draftCells all draft cells
 * @param publishedCells all published cells
 * @returns {*[]} all vertexes that are either new or have been modified
 */
export const getNewOrModifiedVertexes = (draftCells, publishedCells) => {
    const newOrModifiedVertexes = [];

    const publishedVertexIds = getAllVertexes(publishedCells).map((vertex) => vertex.id);
    const draftVertexes = getAllVertexes(draftCells);

    draftVertexes.forEach((vertex) => {
        if (isNewOrModified(vertex, publishedVertexIds)) {
            const vertexId = vertex.id;
            const vertexType = vertex.type;

            if (NON_PROCESS_TYPES.includes(vertexType)) {
                // vertex is a goal, decision diamond or leadsource
                newOrModifiedVertexes.push(vertex);
            } else if (vertexType === FLOW_TYPE) {
                // vertex is a flow
                newOrModifiedVertexes.push(vertex);

                // include processes
                const children = findChildren(vertexId, draftCells);
                const startNodes = children.filter((child) => child.type === SEQUENCE_START);

                startNodes.forEach((startNode) => {
                    if (isNewOrModified(startNode, publishedVertexIds)) {
                        newOrModifiedVertexes.push(startNode);
                    }

                    let flowNode = startNode;
                    let outgoingVertexes = [];

                    do {
                        outgoingVertexes = findOutgoingCells(flowNode.id, draftCells);

                        if (outgoingVertexes.length) {
                            [flowNode] = outgoingVertexes;

                            if (isNewOrModified(flowNode, publishedVertexIds)) {
                                newOrModifiedVertexes.push(flowNode);
                            } else if (flowNode.value?.ready !== '1' && TIMER_TYPES.includes(flowNode.type)) {
                                // we don't care for anything following a timer that is not ready
                                break;
                            }
                        } else {
                            // nothing left to process
                            break;
                        }
                    } while (outgoingVertexes.length);
                });
            }
        }
    });

    return newOrModifiedVertexes;
};

/**
 * Evaluates draft cells to find vertexes that have been removed.
 *
 * @param draftCells all draft cells
 * @param publishedCells all published cells
 * @returns {boolean} true if there are removed vertexes, false otherwise
 */
export const hasRemovedVertexes = (draftCells, publishedCells) => {
    if (!publishedCells?.length) return false;

    const publishedVertexIds = getAllVertexes(publishedCells).map((vertex) => vertex.id);
    const draftReadyVertexIds = getDraftReadyVertexIds(draftCells);

    return publishedVertexIds.some((id) => (!draftReadyVertexIds.includes(id)));
};

/**
 * Marks a given vertex as published, and resets its broken flag.
 *
 * @param vertex the vertex to mark as published and not broken
 */
const markPublishedAndRemoveBrokenFlag = (vertex) => {
    vertex.value.published = '1';
    vertex.value.broken = '0';
};

/**
 * Returns whether the goal is the left-most goal (not preceeded by any other elements).
 *
 * @param cells all cells
 * @param goal the goal to evaluate
 * @returns {boolean} true if the goal is the first element in a chain of elements, or if the goal only follows a single
 * lead source element. False otherwise
 */
const isLeftMostGoal = (cells, goal) => {
    const incomingVertexes = findIncomingCells(goal.id, cells);
    const incomingLeadSources = incomingVertexes.filter((vertex) => vertex.type === LEADSOURCE_TYPE);

    return incomingVertexes.length === incomingLeadSources.length || incomingVertexes.length === 0;
};

/**
 * Returns whether the goal with the given meta type needs to be preceeded by a flow.
 *
 * @param metaType the goal's meta type
 * @returns {boolean} true if the goal needs to be preceeded by a flow, false otherwise
 */
const mustBePrecededByFlow = (metaType) => {
    return metaType === GOAL_EMAIL_LINK_CLICKED || metaType === GOAL_TASK_COMPLETED;
};

/**
 * Evaluates whether a given goal needs to be defaulted to global (marked as achievable by anyone).
 *
 * A goal needs to be marked as global if it must not be preceeded by a flow, and is also the left-most goal (not
 * preceeded by any other elements other than a lead source element.
 *
 * @param cells all cells
 * @param goal the goal to evaluate
 * @returns {boolean} true if the goal needs to be defaulted to global, false otherwise
 */
const needsToDefaultToGlobal = (cells, goal) => {
    return !mustBePrecededByFlow(goal.type) && isLeftMostGoal(cells, goal);
};

/**
 * Sets a given goal's global flag as true if required.
 *
 * @param cells alls cells
 * @param goal the goal to default to global if required
 */
const defaultToGlobalIfNecessary = (cells, goal) => {
    if (needsToDefaultToGlobal(cells, goal)) {
        goal.value.global = '1';
    }
};

/**
 * Marks vertexes that are valid and ready as published.
 *
 * @param draftCells draft cells
 */
export const markVertexesAsPublished = (draftCells) => {
    const draftVertexes = draftCells.filter((cell) => Number(cell.id) > 1 && cell.meta?.vertex === '1');

    // mark everything as not published
    draftVertexes.forEach((vertex) => {
        vertex.value.published = '0';
    });

    // now mark as much published as we can
    draftVertexes
        .filter((vertex) => vertex.value?.ready === '1')
        .forEach((vertex) => {
            const cellType = vertex.type;

            if (cellType === LEADSOURCE_TYPE || cellType === DECISION_TYPE) {
                // vertex is decision diamond or leadsource
                markPublishedAndRemoveBrokenFlag(vertex);
            } else if (Object.values(GOALS).includes(cellType)) {
                // vertex is a goal
                defaultToGlobalIfNecessary(draftCells, vertex);
                markPublishedAndRemoveBrokenFlag(vertex);
            } else if (cellType === FLOW_TYPE) {
                // vertex is a flow
                markPublishedAndRemoveBrokenFlag(vertex);

                // process children
                const children = findChildren(vertex.id, draftCells);
                const startNodes = children.filter((child) => child.type === SEQUENCE_START);

                startNodes.forEach((startNode) => {
                    if (startNode.value?.ready === '1') {
                        markPublishedAndRemoveBrokenFlag(startNode);
                    }

                    let flowNode = startNode;
                    let outgoingVertexes = [];

                    do {
                        outgoingVertexes = findOutgoingCells(flowNode.id, draftCells);

                        if (outgoingVertexes.length) {
                            [flowNode] = outgoingVertexes;

                            if (flowNode.value?.ready === '1') {
                                markPublishedAndRemoveBrokenFlag(flowNode);
                            } else if (flowNode.value?.ready !== '1' && TIMER_TYPES.includes(flowNode.type)) {
                                // we don't care for anything following a timer that is not ready
                                break;
                            }
                        } else {
                            // nothing left to process
                            break;
                        }
                    } while (outgoingVertexes.length);
                });
            }
        });
};

/**
 * Finds all cells that should be published. Makes sure to filter out orphaned edges.
 *
 * @param unpublishableVertexes vertexes that are unpublishable
 * @param cells all cells to publish
 * @returns {*[]} the sanitized cells to publish, with orphan edges removed
 */
export const findPublishableCells = (unpublishableVertexes, cells) => {
    const publishableCells = cells.filter((cell) => {
        return !unpublishableVertexes.includes(cell);
    });
    const cellsToPublish = [];

    publishableCells.forEach((node) => {
        if (node.type === EDGE_TYPE) {
            const source = publishableCells.find((cell) => cell.id === node.meta?.source);
            const target = publishableCells.find((cell) => cell.id === node.meta?.target);

            if (source && target) {
                cellsToPublish.push(node);
            }
        } else {
            cellsToPublish.push(node);
        }
    });

    return cellsToPublish;
};
